import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";

/**
 * Fires once per authenticated session (and once more if the user logs out and
 * back in within the same browser session).  The actual idempotency guard lives
 * in the database function — calling the RPC a second time on the same UTC day
 * returns { already_claimed: true } and is a no-op.
 *
 * Call this hook at the top of a component that always mounts when the user is
 * signed in (e.g. AuthGuard's child tree, or a layout wrapper).
 */
export function useDailyLoginBonus() {
  const { t } = useLocale();
  // Track which user IDs we've already attempted a claim for in this page session,
  // so rapid re-mounts (StrictMode double-invoke, hot reload, etc.) don't cause
  // visible duplicate toasts.
  const attemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Trigger on initial sign-in and on token refresh (covers page reload while
        // already authenticated, which fires TOKEN_REFRESHED before SIGNED_IN).
        if (
          (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") &&
          session?.user
        ) {
          const userId = session.user.id;

          // Deduplicate within this page lifetime
          if (attemptedRef.current.has(userId)) return;
          attemptedRef.current.add(userId);

          // Fire-and-forget — must not block the auth flow.
          // Cast through `any` because this RPC is not yet in the generated
          // Supabase types (added in a recent migration; types regenerate
          // asynchronously).
          (async () => {
            try {
              const { data } = await (supabase.rpc as any)(
                "claim_daily_login_bonus",
                { _user_id: userId },
              );
              const result = data as { granted?: boolean; credits?: number } | null;
              if (result?.granted) {
                toast.success(tFormat(t.auth_daily_bonus, { credits: String(result.credits ?? 0) }), {
                  duration: 3000,
                });
              }
            } catch {
              // Silently ignore network or DB errors — bonus is non-critical
            }
          })();
        }

        // Reset tracking on sign-out so a subsequent sign-in triggers a fresh attempt
        if (event === "SIGNED_OUT") {
          attemptedRef.current.clear();
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);
}
